import React, { useEffect, useMemo, useState } from 'react';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useI18n } from '../i18n/I18nProvider';

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const VocabularySensePickerModal = ({
  isOpen,
  onClose,
  folders = [],
  initialFolderId = '',
  requireListDetails = false,
  heading = 'Add vocabulary',
  onSubmit,
}) => {
  const { t } = useI18n();
  const [folderId, setFolderId] = useState(initialFolderId);
  const [listTitle, setListTitle] = useState('');
  const [rawText, setRawText] = useState('');
  const [items, setItems] = useState([]);
  const [selections, setSelections] = useState({});
  const [phase, setPhase] = useState('input');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setFolderId(initialFolderId || folders[0]?._id || '');
    setListTitle('');
    setRawText('');
    setItems([]);
    setSelections({});
    setPhase('input');
    setError('');
  }, [isOpen, initialFolderId, folders]);

  const selectedEntries = useMemo(() => {
    const entries = [];
    const seen = new Set();
    items.forEach((item) => {
      const selected = selections[item.entry_id] || [];
      selected.forEach((senseId) => {
        if (!(item.senses || []).some((sense) => sense.sense_id === senseId)) return;
        const key = `${String(item.word || '').toLowerCase()}::${senseId}`;
        if (seen.has(key)) return;
        seen.add(key);
        entries.push({ word: item.word, sense_id: senseId });
      });
    });
    return entries;
  }, [items, selections]);

  if (!isOpen) return null;

  const inspect = async () => {
    setError('');
    if (requireListDetails && !listTitle.trim()) {
      setError(t('Give this list a name.'));
      return;
    }
    if (!rawText.trim()) {
      await onSubmit?.({ folderId, listTitle: listTitle.trim(), entries: [] });
      return;
    }
    setPhase('checking');
    try {
      const response = await fetch('/api/vocabulary/inspect', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ raw_text: rawText }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.message || t('This list could not be checked.'));
      const nextItems = Array.isArray(payload.items) ? payload.items : [];
      const nextSelections = {};
      nextItems.forEach((item) => {
        const available = new Set((item.senses || []).map((sense) => sense.sense_id));
        const recommended = Array.isArray(item.recommended_sense_ids)
          ? item.recommended_sense_ids
          : [item.recommended_sense_id].filter(Boolean);
        nextSelections[item.entry_id] = recommended.filter((senseId) => available.has(senseId));
      });
      setItems(nextItems);
      setSelections(nextSelections);
      setPhase('review');
    } catch (requestError) {
      setError(requestError.message || t('This list could not be checked.'));
      setPhase('input');
    }
  };

  const save = async () => {
    setError('');
    setPhase('saving');
    try {
      await onSubmit?.({ folderId, listTitle: listTitle.trim(), entries: selectedEntries });
    } catch (saveError) {
      setError(saveError.message || t('Could not save these meanings.'));
      setPhase('review');
    }
  };

  const toggle = (entryId, senseId) => {
    setSelections((previous) => {
      const current = previous[entryId] || [];
      return {
        ...previous,
        [entryId]: current.includes(senseId)
          ? current.filter((value) => value !== senseId)
          : [...current, senseId],
      };
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-3">
      <div className="flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg bg-white shadow-2xl">
        <header className="flex items-center justify-between border-b px-5 py-4">
          <div>
            <h2 className="text-xl font-bold text-gray-950">{t(heading)}</h2>
            <p className="mt-1 text-sm text-gray-600">
              {t('Choose the exact meanings you want to keep.')}
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-2 text-gray-500 hover:text-gray-900" title={t('Close')}>
            <XMarkIcon className="h-6 w-6" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-5">
          {requireListDetails && (
            <div className="mb-5 grid gap-4 md:grid-cols-2">
              <label className="text-sm font-medium text-gray-800">
                {t('Folder')}
                <select value={folderId || initialFolderId} onChange={(event) => setFolderId(event.target.value)} className="mt-1 w-full rounded border border-gray-300 bg-white p-2.5">
                  {folders.map((folder) => <option data-i18n-skip key={folder._id} value={folder._id}>{folder.title}</option>)}
                </select>
              </label>
              <label className="text-sm font-medium text-gray-800">
                {t('List name')}
                <input value={listTitle} onChange={(event) => setListTitle(event.target.value)} className="mt-1 w-full rounded border border-gray-300 p-2.5" placeholder={t('For example: Reading mistakes')} />
              </label>
            </div>
          )}

          {phase !== 'review' && phase !== 'saving' ? (
            <label className="block text-sm font-medium text-gray-800">
              {t('Words or words with meanings')}
              <textarea
                value={rawText}
                onChange={(event) => setRawText(event.target.value)}
                className="mt-1 min-h-44 w-full rounded border border-gray-300 p-3 leading-7"
                placeholder={'apple\ndraft 征兵\nbank 河岸'}
                disabled={phase === 'checking'}
              />
              <span className="mt-2 block text-xs font-normal text-gray-500">
                {t('Only meanings already available in the public dictionary can be saved.')}
              </span>
            </label>
          ) : (
            <div className="divide-y border border-gray-200">
              {items.map((item) => (
                <section key={item.entry_id} className="grid gap-3 p-4 md:grid-cols-[160px_minmax(0,1fr)]">
                  <div>
                    <strong data-i18n-skip className="text-gray-950">{item.word || item.headword}</strong>
                    {item.meaning_hint && <p data-i18n-skip className="mt-1 text-xs text-gray-500">{item.meaning_hint}</p>}
                  </div>
                  {(item.senses || []).length ? (
                    <div className="divide-y border border-gray-200">
                      {(item.senses || []).map((sense) => {
                        const checked = (selections[item.entry_id] || []).includes(sense.sense_id);
                        return (
                          <label key={sense.sense_id} className={`flex cursor-pointer gap-3 p-3 ${checked ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>
                            <input type="checkbox" checked={checked} onChange={() => toggle(item.entry_id, sense.sense_id)} className="mt-1 h-4 w-4 text-blue-600" />
                            <span>
                              <strong data-i18n-skip className="text-sm text-gray-900">{sense.definition_cn || sense.definition_en}</strong>
                              {sense.is_primary && <span className="ml-2 text-xs font-semibold text-blue-700">{t('Primary')}</span>}
                              {sense.definition_en && <span data-i18n-skip className="mt-1 block text-xs text-gray-600">{sense.definition_en}</span>}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                      {t('No matching public meaning was found. This entry will be skipped.')}
                    </div>
                  )}
                </section>
              ))}
              {items.length === 0 && <p className="p-4 text-gray-500">{t('No vocabulary entries were found.')}</p>}
            </div>
          )}
          {error && <p role="alert" className="mt-4 border-l-4 border-red-500 bg-red-50 p-3 text-sm text-red-800">{error}</p>}
        </div>

        <footer className="flex items-center justify-between border-t bg-gray-50 px-5 py-4">
          <span className="text-sm text-gray-600">
            {(phase === 'review' || phase === 'saving') && t('{count} meanings selected', { count: selectedEntries.length })}
          </span>
          <div className="flex gap-3">
            {(phase === 'review' || phase === 'saving') && (
              <button type="button" onClick={() => setPhase('input')} disabled={phase === 'saving'} className="rounded border border-gray-300 bg-white px-4 py-2 text-gray-800 hover:bg-gray-100">
                {t('Back')}
              </button>
            )}
            <button type="button" onClick={onClose} className="rounded border border-gray-300 bg-white px-4 py-2 text-gray-800 hover:bg-gray-100">{t('Cancel')}</button>
            {phase === 'review' || phase === 'saving' ? (
              <button type="button" onClick={save} disabled={phase === 'saving'} className="inline-flex items-center gap-2 rounded bg-blue-600 px-5 py-2 font-semibold text-white hover:bg-blue-700 disabled:opacity-60">
                <CheckIcon className="h-5 w-5" />
                {phase === 'saving' ? t('Saving...') : t('Save selected meanings')}
              </button>
            ) : (
              <button type="button" onClick={inspect} disabled={phase === 'checking'} className="rounded bg-blue-600 px-5 py-2 font-semibold text-white hover:bg-blue-700 disabled:opacity-60">
                {phase === 'checking' ? t('Checking meanings...') : (rawText.trim() ? t('Check meanings') : t('Create empty list'))}
              </button>
            )}
          </div>
        </footer>
      </div>
    </div>
  );
};

export default VocabularySensePickerModal;
